/**
 * Agent Action Service
 * Provides a generic way for the floating agent to interact with any UI element
 * using data-agent-action attributes
 */

class AgentActionService {
  /**
   * Execute an action by finding elements with the matching data-agent-action attribute
   * @param {string} actionName - The action name to execute
   * @returns {boolean} - Whether the action was successfully executed
   */
  executeAction(actionName) {
    try {
      // Find all elements with the matching data-agent-action attribute
      const elements = document.querySelectorAll(
        `[data-agent-action="${actionName}"]`
      );

      if (elements.length === 0) {
        console.warn(
          `Agent Action: No elements found with action "${actionName}"`
        );
        return false;
      }

      // Execute action on the first matching element
      const element = elements[0];

      if (element.tagName === "BUTTON" || element.tagName === "A") {
        // For buttons and links, trigger a click
        element.click();
        console.log(
          `Agent Action: Clicked element with action "${actionName}"`
        );
        return true;
      } else if (
        element.tagName === "INPUT" ||
        element.tagName === "TEXTAREA"
      ) {
        // For inputs, focus the element
        element.focus();
        console.log(`Agent Action: Focused input with action "${actionName}"`);
        return true;
      } else if (element.tagName === "SELECT") {
        // For selects, open the dropdown
        element.focus();
        element.click();
        console.log(`Agent Action: Opened select with action "${actionName}"`);
        return true;
      } else {
        // For other elements, try to trigger a click event
        const clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true,
        });
        element.dispatchEvent(clickEvent);
        console.log(
          `Agent Action: Triggered click event on element with action "${actionName}"`
        );
        return true;
      }
    } catch (error) {
      console.error(
        `Agent Action: Error executing action "${actionName}":`,
        error
      );
      return false;
    }
  }

  /**
   * Get all available actions in the current DOM
   * @returns {Array} - Array of available action names
   */
  getAvailableActions() {
    const elements = document.querySelectorAll("[data-agent-action]");
    const actions = Array.from(elements).map((el) =>
      el.getAttribute("data-agent-action")
    );
    return [...new Set(actions)]; // Remove duplicates
  }

  /**
   * Check if an action is available
   * @param {string} actionName - The action name to check
   * @returns {boolean} - Whether the action is available
   */
  isActionAvailable(actionName) {
    const elements = document.querySelectorAll(
      `[data-agent-action="${actionName}"]`
    );
    return elements.length > 0;
  }

  /**
   * Register a custom action handler
   * @param {string} actionName - The action name
   * @param {Function} handler - The handler function
   */
  registerCustomAction(actionName, handler) {
    if (!this.customActions) {
      this.customActions = new Map();
    }
    this.customActions.set(actionName, handler);
  }

  /**
   * Execute a custom action if registered
   * @param {string} actionName - The action name
   * @returns {boolean} - Whether the custom action was executed
   */
  executeCustomAction(actionName) {
    if (this.customActions && this.customActions.has(actionName)) {
      try {
        this.customActions.get(actionName)();
        console.log(`Agent Action: Executed custom action "${actionName}"`);
        return true;
      } catch (error) {
        console.error(
          `Agent Action: Error executing custom action "${actionName}":`,
          error
        );
        return false;
      }
    }
    return false;
  }

  /**
   * Main execute method that tries both DOM and custom actions
   * @param {string} actionName - The action name to execute
   * @returns {boolean} - Whether any action was executed
   */
  execute(actionName) {
    // Try custom actions first
    if (this.executeCustomAction(actionName)) {
      return true;
    }

    // Fall back to DOM-based actions
    return this.executeAction(actionName);
  }

  // Appointment-specific actions

  /**
   * Show today's schedule by scrolling to or highlighting today's appointments
   */
  showTodaysSchedule() {
    // Try to find today's schedule widget in sidebar first
    const todaysSchedule = document.querySelector(
      '[data-testid="todays-schedule"]'
    );
    if (todaysSchedule) {
      todaysSchedule.scrollIntoView({ behavior: "smooth", block: "center" });
      // Add a brief highlight effect
      todaysSchedule.classList.add(
        "ring-2",
        "ring-blue-400",
        "ring-opacity-75"
      );
      setTimeout(() => {
        todaysSchedule.classList.remove(
          "ring-2",
          "ring-blue-400",
          "ring-opacity-75"
        );
      }, 2000);
      return true;
    }

    // If not found, navigate to appointments page
    window.location.href = "/appointments";
    return true;
  }

  /**
   * Create a new visit for a specific patient
   * @param {string} patientId - The patient ID
   */
  createNewVisit(patientId) {
    // Look for existing "New Visit" button first
    const newVisitBtn = document.querySelector(
      '[data-agent-action="new-visit"]'
    );
    if (newVisitBtn) {
      newVisitBtn.click();
      return true;
    }

    // If no button found, navigate directly
    window.location.href = `/patients/${patientId}?newVisit=true`;
    return true;
  }

  /**
   * Focus on search input
   */
  focusSearch() {
    // Try to find search input
    const searchInput = document.querySelector(
      'input[type="search"], input[placeholder*="search" i], input[placeholder*="Search" i]'
    );
    if (searchInput) {
      searchInput.focus();
      searchInput.select();
      return true;
    }

    // Try to find and click search button
    const searchBtn = document.querySelector('[data-agent-action="search"]');
    if (searchBtn) {
      searchBtn.click();
      return true;
    }

    console.warn("Agent Action: No search input or button found");
    return false;
  }

  /**
   * Navigate to next patient appointment (context-aware)
   */
  nextPatient() {
    // This could be enhanced to find the next scheduled patient
    // For now, navigate to appointments list
    window.location.href = "/appointments";
    return true;
  }
}

// Create and export a singleton instance
const agentActionService = new AgentActionService();
export default agentActionService;
